import React, { useEffect, useMemo, useState } from 'react';
import { createMuiTheme, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ThemeProvider, Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import './ModalPendingEligibility.scss';
import { sortTablesByType } from '../../../../services/sortsUtil';
import { ReactComponent as IconSort } from '../../../../assets/icons/u_sort.svg';
import { ReactComponent as IconSearch } from '../../../../assets/icons/u_new_search-plus.svg';
import { ReactComponent as IconError } from '../../../../assets/icons/u_error.svg';
import { Pagination } from '@material-ui/lab';
import { ProjectsListSkeleton } from '../../../LoadersSkeletons/ProjectsListSkeleton/ProjectsListSkeleton';
import ModalRowsSelect from './ModalRowsSelect';
import { withStyles } from '@material-ui/core/styles';

 const BlackTooltip = withStyles({
    popper: {
    zIndex: 2147483647,    
  },
    tooltip: {
      backgroundColor: '#101319',
      fontSize: '16px',
      fontFamily: 'Rubik',
      lineHeight: '20px',
      padding: '8px 12px',
      borderRadius: '8px',    
    },
    arrow: {
      fontSize: 20,
      color: '#4A4A4A',
      '&::before': {
        backgroundColor: '#101319',
      },
    },
  })(Tooltip);
const dropdownFixTheme = createMuiTheme({
  props: {
    MuiSelect: {
      MenuProps: {
        disablePortal: true,              
        getContentAnchorEl: null,            
        anchorOrigin: { vertical: 'top', horizontal: 'left' },        
        transformOrigin: { vertical: 'bottom', horizontal: 'left' },  
        PaperProps: { style: { zIndex: 2005, maxHeight: 260 } },      
      } as any,
    },
    MuiMenu: {
      getContentAnchorEl: null,
    } as any,
  },
});


type Row = {
  stateCdId: string;
  clauseNumber: string;
  name: string;
  categoryName: string;
  currentExpirationDate: string | Date | null;
  approvalDate?: string | Date | null;
  state: string;
};

function parseDate(d: string | Date | null) {
  if (!d) return null;
  const dt = typeof d === 'string' ? new Date(d) : d;
  return isNaN(+dt) ? null : dt;
}

function formatDate(d: string | Date | null) {
  const dt = parseDate(d);
  if (!dt) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(dt);
}


const normalizeCode = (s?: string) => (s ?? '').trim().toUpperCase();

function getExpirationClass(expiration: string | Date | null, stateCdId?: string) {
  if (normalizeCode(stateCdId) === 'CL_FU') return 'date-regular';

  const dt = parseDate(expiration);
  if (!dt) return 'date-regular';
  const today = new Date();
  const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
  const nextWeek = new Date(endOfToday);
  nextWeek.setDate(nextWeek.getDate() + 7);

  if (dt < endOfToday) return 'date-bold-red';  
  if (dt <= nextWeek) return 'date-bold';         
  return 'date-regular';                           
}

type TableProps = {
  clausesData?: Row[];
  loading?: boolean;
  error?: boolean;
  noResults?: boolean;
  onRetry?: () => void;
  onCancel?: () => void;
  searchText?: string
};

const TableModalPendingEligibility = ({
  clausesData = [],
  loading = false,
  error = false,
  noResults,
  onRetry,
  onCancel,
  searchText = '',
}: TableProps) => {
  const { t } = useTranslation();
const [sortAsc, setSortAsc] = useState(true);
const [page, setPage] = useState(1);
const [rowsPerPage, setRowsPerPage] = useState(6);
const [data, setData] = useState<Row[]>(() => [...clausesData]);
const start = (page - 1) * rowsPerPage;

const sortTable = (keyToSort: string, fieldType: string) => {
  const next = [...data];                   
  sortTablesByType(next, sortAsc, keyToSort, fieldType);
  setData(next);                           
  setSortAsc(!sortAsc);
  setPage(1);                              
};

const pagesCount = Math.max(1, Math.ceil(data.length / rowsPerPage));

const pagedRows = useMemo(
  () => data.slice(start, start + rowsPerPage),
  [data, start, rowsPerPage]
);

useEffect(() => {
  setData([...clausesData]);  
  setPage(1);
}, [clausesData]);

useEffect(() => {
  if (page > pagesCount) setPage(pagesCount);
}, [pagesCount, page]);

const handlePaginationChange = (_e: any, value: number) => setPage(value);
const handleChangeRows = (n: number) => { setRowsPerPage(n); setPage(1); };
const offline = typeof navigator !== 'undefined' && !navigator.onLine;

const showLoading   = !!loading;
const showError     = !showLoading && (offline || !!error);
const showNoResults = !showLoading && !showError && noResults === true;
const showNoData    = !showLoading && !showError && !showNoResults && data.length === 0;
const isAnyState = showLoading || showError || showNoResults || showNoData;

  const COLS = 6; 

  const RowWrapper = ({ children }: { children: React.ReactNode }) => (
  <TableRow className="elig-state-row">
    <TableCell colSpan={COLS}>
      <div className="elig-state-wrap">{children}</div>
    </TableCell>
  </TableRow>
);

const renderLoading = () => (
  <RowWrapper><ProjectsListSkeleton /></RowWrapper>
)

const renderError = () => (
  <RowWrapper>
    <div className="elig-state-wrap">
      <div className="elig-empty-wrap">
        <div className='elig-empty-icon'><IconError/></div>
        <h4 className='elig-empty-title'>{t('new_elig_error_title')}</h4>
        <p className="elig-state-text"> {t('new_elig_error_message')}</p>
      <div className="elig-actions">
        <button className="btn-outlined" onClick={onCancel}>{t('cancel')}</button>
        <button className="btn-primary" onClick={onRetry}>{t('new_elig_button_retry')}</button>
        </div>
      </div>
    
    </div>
  </RowWrapper>
);
  const prettyQuery = (searchText?.trim()?.length ? `“${searchText.trim()}”` : 'tu búsqueda');

const renderNoResults = () => (
  <RowWrapper>
    <div className="elig-state-wrap">
      <div className="elig-empty-wrap">
        <div className="elig-empty-icon"><IconSearch/></div>
         <h4 className="elig-empty-title">{t('new_elig_no_results_title')} {prettyQuery}</h4>
        <p className="elig-state-text">{t('new_elig_no_results_message')}</p>
      </div>
    </div>
  </RowWrapper>
);

return (
  <div className="elig-table-wrap">
    <div className={`elig-scroll borderTable ${isAnyState ? 'no-scroll' : ''}`}>
      <TableContainer className="elig-container">
        <Table className={`elig-table ${isAnyState ? 'compact' : ''}`}>
        <colgroup>
          <col style={{ width: 'var(--w-col-clause)' }} />
          <col style={{ width: 'var(--w-col-name)'   }} />
          <col style={{ width: 'var(--w-col-cat)'    }} />
          <col style={{ width: 'var(--w-col-exp)'    }} />
          <col style={{ width: 'var(--w-col-exp)'    }} />
          <col style={{ width: 'var(--w-col-status)' }} />
        </colgroup>

          <TableHead>
            <TableRow>
              <TableCell onClick={() => sortTable('clauseNumber', 'string')}>
                <div className="text-head"><div>{t('new_elig_column_clause')}</div><div><IconSort className="sortIcon" /></div></div>
              </TableCell>
              <TableCell onClick={() => sortTable('name', 'string')}>
                <div className="text-head"><div>{t('name')}</div><div><IconSort className="sortIcon" /></div></div>
              </TableCell>
              <TableCell onClick={() => sortTable('categoryName', 'string')}>
                <div className="text-head"><div>{t('category')}</div><div><IconSort className="sortIcon" /></div></div>
              </TableCell>
              <TableCell onClick={() => sortTable('currentExpirationDate', 'date')}>                
                <div className="text-head"><div> {t('new_elig_column_expiration')}</div><div><IconSort className="sortIcon" /></div></div>
              </TableCell>
              <TableCell onClick={() => sortTable('approvalDate', 'date')}>
                <div className="text-head"><div>{t('new_elig_column_approval')}</div><div><IconSort className="sortIcon" /></div></div>
              </TableCell>
              <TableCell onClick={() => sortTable('state', 'string')}>
                <div className="text-head"><div>{t('status')}</div><div><IconSort className="sortIcon" /></div></div>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {showLoading   && renderLoading()}
            {showError     && renderError()}
            {showNoResults && renderNoResults()}
            {showNoData    && renderLoading()}

            {!isAnyState && pagedRows.map((row, i) => (
              <TableRow key={i}>
                <TableCell className="cell-number">  <span className="cell-number__text">{row.clauseNumber}</span></TableCell>
                <TableCell className="name-cell">
                  <div className="clamp-2 name-content" >
                    
                    <BlackTooltip
                          title={row.name}
                          placement="bottom"
                          arrow
                          enterDelay={500}
                          leaveDelay={100}
                          PopperProps={{
                          style: { zIndex: 20050 },         
                          container: document.body,        
                          }}>
                    <div className="clamp-2 tooltip-target">{row.name}</div>                  
                    </BlackTooltip>
                    </div>        
                </TableCell>
                <TableCell className="category-cell">
                  <div className="clamp-2 name-content" title={row.categoryName}>{row.categoryName}</div>
                </TableCell>
                <TableCell className={`date-cell col-exp ${getExpirationClass(row.currentExpirationDate, row.stateCdId)}`} align="center">
                  {formatDate(row.currentExpirationDate)}
                </TableCell>
                <TableCell className="date-cell col-appr center" align="center">
                  {formatDate(row.approvalDate ?? null)}
                </TableCell>
                <TableCell className="col-status" align="center">{row.state}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>

<ThemeProvider theme={dropdownFixTheme}>
  <div className={`pagination-style ${isAnyState ? 'ghost' : ''}`}>
    <ModalRowsSelect
      page={page}
      pageSize={rowsPerPage}
      total={data.length}
      onChangePageSize={handleChangeRows}
      options={[6, 12, 18, 24]}
    />
    {pagesCount > 1 && (
      <Pagination
        count={pagesCount}
        page={page}
        onChange={handlePaginationChange}
      />
    )}
  </div>
</ThemeProvider>
  </div>
);

};

export default TableModalPendingEligibility;
